import { useEffect, useMemo, useRef, useState } from 'react';
import { Clock3, Headphones, Play, X } from 'lucide-react';
import { getContinueListening, getRecentlyPlayed, readListeningHistory } from '../lib/listeningHistory';
import { thumbUrl } from '../lib/imageUrl';

const selectListeningItems = (history, limit = 12) => {
  const resumable = getContinueListening(history, limit);
  return resumable.length ? resumable : getRecentlyPlayed(history, limit);
};

export default function ContinueListeningMenu({ variant = 'navigation' }) {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState(() => readListeningHistory());
  const panelRef = useRef(null);
  const items = useMemo(() => selectListeningItems(history), [history]);

  useEffect(() => {
    const refresh = () => setHistory(readListeningHistory());
    window.addEventListener('rastyle:listening-history-changed', refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener('rastyle:listening-history-changed', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', closeOutside);
    return () => document.removeEventListener('mousedown', closeOutside);
  }, [open]);

  if (!items.length && variant !== 'action-bar') return null;

  const resume = (item) => {
    window.dispatchEvent(new CustomEvent('rastyle:resume-listening', { detail: item }));
    setOpen(false);
  };

  const actionBar = variant === 'action-bar';

  return (
    <div className="relative min-w-0" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        disabled={!items.length}
        className={actionBar
          ? 'relative flex h-16 w-full min-w-0 flex-col items-center justify-center gap-1 px-1 text-[10px] font-bold uppercase tracking-wide text-zinc-300 transition hover:bg-white/5 hover:text-white disabled:cursor-default disabled:text-zinc-600'
          : 'relative grid h-10 w-10 place-items-center rounded-sm bg-white/5 text-zinc-200 transition hover:bg-white/10 hover:text-rastyle-orange'}
        aria-label={items.length ? 'Continue listening' : 'Continue listening — no saved sessions'}
        aria-expanded={open}
        data-testid="continue-listening-menu-button"
      >
        <Headphones className="h-5 w-5" />
        {actionBar && <span>Continue</span>}
        {items.length > 0 && (
          <span className={actionBar
            ? 'absolute right-[27%] top-1.5 min-w-4 rounded-full bg-rastyle-orange px-1 text-center text-[9px] font-black leading-4 text-black'
            : 'absolute -right-1 -top-1 min-w-4 rounded-full bg-rastyle-orange px-1 text-center text-[9px] font-black leading-4 text-black'}>
            {Math.min(items.length, 9)}
          </span>
        )}
      </button>

      {open && (
        <div
          className={actionBar
            ? 'fixed inset-x-3 z-[90] max-h-[65dvh] overflow-y-auto overscroll-contain rounded-2xl border border-white/10 bg-zinc-950 p-3 shadow-2xl'
            : 'fixed inset-x-3 top-16 z-[70] max-h-[calc(100dvh-5rem)] overflow-y-auto overscroll-contain border border-white/10 bg-zinc-950 p-3 shadow-2xl sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:max-h-[70vh] sm:w-96'}
          style={actionBar ? { bottom: 'calc(var(--rastyle-mobile-media-stack-height, 8rem) + 0.5rem)' } : undefined}
          data-testid="continue-listening-menu"
        >
          <div className="mb-3 flex items-center justify-between border-b border-white/10 pb-3">
            <h2 className="flex items-center gap-2 font-heading text-sm font-black uppercase tracking-wide">
              <Clock3 className="h-4 w-4 text-rastyle-orange" /> Continue Listening
            </h2>
            <button type="button" onClick={() => setOpen(false)} className="p-1 text-zinc-400 hover:text-white" aria-label="Close continue listening">
              <X className="h-4 w-4" />
            </button>
          </div>
          <ul className="space-y-2">
            {items.map((item) => (
              <li key={item.id}>
                <button type="button" onClick={() => resume(item)} className="flex w-full items-center gap-3 border border-white/5 bg-black/50 p-2 text-left hover:border-rastyle-orange/50">
                  <div className="h-14 w-14 flex-none overflow-hidden bg-zinc-900">
                    {item.cover ? <img src={thumbUrl(item.cover)} alt="" className="h-full w-full object-cover" /> : <span className="grid h-full place-items-center text-zinc-600">♪</span>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-bold text-white">{item.title}</p>
                    <p className="truncate text-xs text-zinc-400">{item.artist || item.creator_slug || item.track_type}</p>
                    {item.duration > 0 && (
                      <div className="mt-2 h-1 overflow-hidden bg-white/10">
                        <div className="h-full bg-rastyle-orange" style={{ width: `${Math.max(2, Math.min(100, (item.position / item.duration) * 100))}%` }} />
                      </div>
                    )}
                  </div>
                  <Play className="h-4 w-4 flex-none fill-current text-rastyle-orange" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export { selectListeningItems };
